import * as React from 'react';
import { Icon } from '@cotera/client/app/components';
import { shareMoveArchiveItems } from '../../resources/list/resource-item-actions';
import type { ItemAction } from '../../resources/list/resource-list-view-types';
import { Caption } from '@cotera/client/app/components/ui/text/caption';
import {
  BookOpen,
  Building2,
  Check,
  ChevronDown,
  ChevronRight,
  ChevronsUpDown,
  CircleHelp,
  CreditCard,
  Ellipsis,
  Ghost,
  LogOut,
  Plus,
  Search,
  Settings,
  Wrench,
} from 'lucide-react';
import { Protected } from '@cotera/client/app/components';
import { useOpenToolsModal } from '@cotera/client/v0/components/tool-connections';
import { useOpenCommandPalette } from '@cotera/client/v0/actions/command-palette';
import { useSettingsModal } from '@cotera/client/v0/settings/settings-modal';
import { useLogout } from '@cotera/client/app/hooks/use-logout';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@cotera/client/v0/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@cotera/client/v0/components/ui/dropdown-menu';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarMenuAction,
  SidebarMenuBadge,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
  useSidebar,
  SidebarSeparator,
} from '@cotera/client/v0/components/ui/sidebar';
import { useMediaQuery } from '@cotera/client/app/hooks';
import { useTenantedClient, useWhoami } from '@cotera/client/app/stores/org';
import { CocoEvent, useAnalytics } from '@cotera/client/app/etc';
import { Link } from 'react-router-dom';
import { useAppModel } from '../app.model';
import {
  ModelScopeFactory,
  useWatchableValue,
  Watchable,
} from '@cotera/client/v0/actions/framework';
import {
  RecentChatModel,
  useRecentChatModel,
} from '../../chat/models/recent-chat.model';
import { useWatchablesFactory } from '../../watchables/watchables-factory-context';
import { useCocoSidebarActive } from './coco-sidebar-active';
import { Favorites } from './coco-sidebar-favorites';
import { collapseTransition, useSidebarSectionOpen } from './sidebar-section';
import { useFavoriteMenuItem } from '../../resources/favorites';
import {
  ResourceSelectionModel,
  ResourceType,
} from '../../resources/selection/resource-selection.model';
import { useResourceListSelection } from '../../resources/selection/use-resource-list-selection';
import { MoveToFolderAction } from '../../resources/actions/move-to-folder.action';
import { ShareResourceLinkAction } from '../../resources/actions/share-resource-link.action';
import { ArchiveResourceAction } from '../../resources/actions/archive-resource.action';
import { useAction } from '@cotera/client/v0/actions/framework';
import { useAuth } from '@workos-inc/authkit-react';
import { useQuery } from '@tanstack/react-query';
import { Assert } from '@cotera/utilities';
import { queryKeys } from '@cotera/client/app/hooks/query-cache-keys';
import { cn, getCmdModifierKeySymbol } from '@cotera/client/v0/lib/utils';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@cotera/client/v0/components/ui/collapsible';
import { ScrollArea } from '@cotera/client/v0/components/ui/scroll-area';
import { Skeleton } from '@cotera/client/v0/components/ui/skeleton';
import { ShimmerText } from '@cotera/client/v0/components/ui/shimmer-text';
import type { RecentChatListItem } from '../app.model';

/**
 * Re-centres a trailing control (`SidebarMenuBadge`, `SidebarMenuAction`) in its
 * row. Both primitives pin themselves near the top of the row, which only lands
 * correctly on a button of the default height — anything taller leaves the
 * control floating above the label it belongs to.
 *
 * Both the bare `top-*` and the `peer-data-[size=default]` one have to be
 * replaced. `twMerge` treats them as separate declarations and keeps whichever it
 * is not given a same-shaped override for, so setting only one leaves the other
 * live and the winner comes down to Tailwind's emit order.
 */
const SIDEBAR_ROW_TRAILING_CENTERED = cn(
  'top-1/2',
  'peer-data-[size=default]/menu-button:top-1/2',
  '-translate-y-1/2'
);

const RECENTS_COOKIE_NAME = 'sidebar_recents_state';

function SidebarToggleIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      <rect x="4" y="5.5" width="16" height="13" rx="2.25" />
      <path d="M8 5.5v13" />
    </svg>
  );
}

function CocoHeader() {
  const { toggleSidebar, open, isMobile, setOpenMobile } = useSidebar();
  const org = useWhoami((s) => s.org);

  const handleHomeClick = React.useCallback(() => {
    if (isMobile) {
      setOpenMobile(false);
    }
  }, [isMobile, setOpenMobile]);

  return (
    <div
      className="flex items-center overflow-hidden whitespace-nowrap"
      style={{
        paddingLeft: open ? 8 : 0,
        transition: collapseTransition('padding'),
      }}
    >
      <Link
        to={`/${org.slug}`}
        onClick={handleHomeClick}
        className="relative top-px text-2xl leading-none font-semibold font-serif text-slate-800 cursor-pointer hover:opacity-80 whitespace-nowrap min-w-0 flex-1 text-left overflow-hidden max-w-full group-data-[collapsible=icon]:max-w-0 group-data-[collapsible=icon]:pointer-events-none group-data-[collapsible=icon]:opacity-0"
        style={{
          transition: collapseTransition('max-width', 'opacity'),
        }}
      >
        Coco
        <Caption className="text-xs text-sidebar-foreground/70">
          By Cotera
        </Caption>
      </Link>
      <button
        onClick={toggleSidebar}
        className="rounded-md hover:bg-sidebar-accent transition-colors cursor-pointer shrink-0"
        style={{
          paddingTop: 8,
          paddingBottom: 8,
          paddingLeft: open ? 8 : 14,
          paddingRight: open ? 8 : 14,
          transition: collapseTransition('padding'),
        }}
        aria-label={open ? 'Close sidebar' : 'Open sidebar'}
      >
        <SidebarToggleIcon className="h-5 w-5" />
      </button>
    </div>
  );
}

function NavMenu() {
  const org = useWhoami((s) => s.org);
  const { open } = useSidebar();
  const isActive = useCocoSidebarActive();
  const openToolsModal = useOpenToolsModal();
  const openCommandPalette = useOpenCommandPalette();
  const analytics = useAnalytics();

  // Collapsed width = 48px. Icon = 20px (size-5). Centered padding = (48 - 20) / 2 = 14px
  const navButtonClass =
    'cursor-pointer [&>svg]:size-5 h-10 [&>span]:group-data-[collapsible=icon]:absolute [&>span]:group-data-[collapsible=icon]:opacity-0 [&>span]:group-data-[collapsible=icon]:pointer-events-none [&>span]:transition-opacity [&>span]:duration-150';

  const navButtonStyle: React.CSSProperties = {
    paddingLeft: open ? 8 : 14,
    paddingRight: open ? 8 : 14,
    transition: collapseTransition('padding'),
  };

  // "New chat" is the one thing in this menu that *starts* something; Search and
  // Library go and look at what already exists. Nothing in the row said so — the
  // three were byte-identical but for their icon, so the primary action was
  // findable only by reading.
  //
  // Most of the weight lands on the glyph: a solid `primary` chip with the plus
  // knocked out of it in `primary-foreground`. No border, so the row keeps the
  // other two's geometry exactly and the menu stays a column of three identical
  // shapes with one filled mark in it.
  //
  // `font-medium` is what the *label* is emphasised with, because `text-primary`
  // cannot do it: `--primary` is `oklch(0.21)` against a `--sidebar-foreground`
  // of `oklch(0.141)`, so tinting the row primary renders it a shade lighter
  // than the Search and Library rows it is meant to lead. This theme has no
  // chromatic accent to reach for — primary *is* the near-black — so the step up
  // has to come from weight. The base row already spends `font-medium` on
  // `data-[active=true]`; here it is unconditional, which costs that row its
  // active-state weight change but buys the emphasis in the nine-tenths of the
  // time the user is somewhere else.
  //
  // The `primary` tint is pinned across hover and press. The base row repaints
  // its label `sidebar-accent-foreground` in both, which in the light theme is
  // the same `oklch(0.21)` as primary and so changes nothing — but in the dark
  // theme it is `oklch(0.985)` against primary's `oklch(0.92)`, so the label
  // would brighten out of its own colour the moment the pointer touched it.
  // `cn` merges these last, which drops the base's versions rather than racing
  // them on specificity.
  const ctaButtonClass = cn(
    navButtonClass,
    'font-medium text-primary hover:text-primary active:text-primary'
  );

  const trackNavClick = (destination: string) => {
    analytics.track(CocoEvent.SidebarNavClicked, {
      destination,
      orgId: org.id,
    });
  };

  return (
    <SidebarMenu className="gap-1 px-0 py-1">
      <SidebarMenuItem>
        <SidebarMenuButton
          asChild
          className={ctaButtonClass}
          style={navButtonStyle}
          // No `isActive`, deliberately. The selected fill answers "where am
          // I"; this row answers "start something", and the two readings fight
          // as soon as the row is both. They are both here constantly:
          // `new-chat` is active on `/{org.slug}`, the landing route, so the
          // fill was the CTA's *resting* appearance for anyone who had just
          // opened the app and the accent then meant nothing at all. It is also
          // the same accent the other rows use for hover, so a permanently
          // filled CTA reads as a row stuck mid-hover.
          //
          // The chip and the medium label are what mark this row now, and they
          // do it in every location rather than one. Hover and press keep their
          // backgrounds — those are feedback on a gesture, not a claim about
          // where the user is.
          tooltip="New chat"
        >
          <Link to={`/${org.slug}`} onClick={() => trackNavClick('new-chat')}>
            {/* A `div`, not a `span`: {@link navButtonClass} collapses every
                direct-child span to `opacity-0` in the icon rail so the labels
                disappear, and the chip is the one thing in this row that has to
                survive that. Sized to the 20px the bare icons occupy, so the
                mark sits on the same vertical line as Search's and Library's
                glyphs in both widths. */}
            <div className="flex size-5 shrink-0 items-center justify-center rounded-md bg-primary text-primary-foreground">
              <Plus className="size-3.5" strokeWidth={2.5} />
            </div>
            <span>New chat</span>
          </Link>
        </SidebarMenuButton>
      </SidebarMenuItem>
      <SidebarMenuItem>
        <SidebarMenuButton
          className={navButtonClass}
          style={navButtonStyle}
          tooltip="Search"
          onClick={() => {
            trackNavClick('search');
            openCommandPalette();
          }}
        >
          <Search className="h-5 w-5" />
          <span>Search</span>
        </SidebarMenuButton>
        {/* The palette has always been on this shortcut; nothing said so. The
            badge hides itself in the rail, where there is no room for it. */}
        <SidebarMenuBadge
          className={cn(
            SIDEBAR_ROW_TRAILING_CENTERED,
            'text-sidebar-foreground/50'
          )}
        >
          {getCmdModifierKeySymbol()}K
        </SidebarMenuBadge>
      </SidebarMenuItem>
      <SidebarMenuItem>
        <SidebarMenuButton
          asChild
          className={navButtonClass}
          style={navButtonStyle}
          isActive={isActive({ type: 'library' })}
          tooltip="Library"
        >
          <Link
            to={`/${org.slug}/folders`}
            onClick={() => trackNavClick('library')}
          >
            <BookOpen className="h-5 w-5" />
            <span>Library</span>
          </Link>
        </SidebarMenuButton>
      </SidebarMenuItem>
      <SidebarMenuItem>
        <SidebarMenuButton
          asChild
          className={navButtonClass}
          style={navButtonStyle}
          isActive={isActive({ type: 'workflows' })}
          tooltip="Automations"
        >
          <Link
            to={`/${org.slug}/workflows`}
            onClick={() => trackNavClick('workflows')}
          >
            <Icon icon="workflow" className="h-5 w-5" />
            <span>Automations</span>
          </Link>
        </SidebarMenuButton>
      </SidebarMenuItem>
      {/* Datasets is browsed from the Library (alongside Documents), not from a
          top-level nav entry — it was reachable both ways, showing overlapping
          content under different chrome. Automations keeps its entry: it's a
          surface worth top-level prominence. The `/datasets` routes remain for
          deep links and resource breadcrumbs. */}
      <SidebarMenuItem>
        <SidebarMenuButton
          className={navButtonClass}
          style={navButtonStyle}
          tooltip="Tools"
          onClick={() => {
            trackNavClick('tools');
            openToolsModal();
          }}
        >
          <Wrench className="h-5 w-5" />
          <span>Tools</span>
        </SidebarMenuButton>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

function RecentChatsContent() {
  const appModel = useAppModel();
  const recentConversations = useWatchableValue(appModel.recentChats);
  const client = useTenantedClient();
  const watchablesFactory = useWatchablesFactory();

  // Stabilise deps so models are only recreated when the *set* of conversation
  // IDs changes, not when titles or other properties update. Recreating models
  // on every title change resets the shimmer animation and causes flashing.
  const conversationIds = recentConversations.map((c) => c.id);
  const stableIdsKey = conversationIds.join(',');

  const models = React.useCallback(() => {
    return [
      ...recentConversations.map(
        (conversation) =>
          new RecentChatModel({
            conversationId: conversation.id,
            client: client.resource.chat,
            watchablesFactory,
          })
      ),
      // Share / move / archive read their target from the shared selection
      // model, and the app scope has none — it is normally provided per list.
      // Without one here the row menu's actions would silently no-op.
      new ResourceSelectionModel(),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stableIdsKey, client.resource.chat, watchablesFactory]);

  if (recentConversations.length === 0) {
    return (
      <p className="px-2 py-1.5 text-xs text-muted-foreground">
        No chats yet. Start one above.
      </p>
    );
  }

  return (
    <ModelScopeFactory
      createModels={models}
      deps={[stableIdsKey, client.resource.chat, watchablesFactory]}
    >
      <SidebarMenu>
        {recentConversations.map((conversation) => (
          <RecentChatsMenuItem
            key={conversation.id}
            conversation={conversation}
          />
        ))}
      </SidebarMenu>
    </ModelScopeFactory>
  );
}

const FALSE_WATCHABLE = Watchable.fromValue(false);

function RecentChatsMenuItem({
  conversation,
}: {
  conversation: RecentChatListItem;
}) {
  const org = useWhoami((s) => s.org);
  const isActive = useCocoSidebarActive();
  const model = useRecentChatModel(conversation.id);
  const isReplying = useWatchableValue(
    model?.isReplyingWatchable ?? FALSE_WATCHABLE
  );
  const { handleOpenActionMenu } = useResourceListSelection();
  const client = useTenantedClient();
  const shareAction = useAction(ShareResourceLinkAction);
  const moveToFolderAction = useAction(MoveToFolderAction);
  const archiveResourceAction = useAction(ArchiveResourceAction);
  const favoriteMenuItem = useFavoriteMenuItem();

  // Point the shared actions at this chat before running one. Done on open
  // rather than on click so the target is pinned to the row whose menu is
  // showing, matching how the list rows do it.
  const claimSelection = React.useCallback(() => {
    handleOpenActionMenu({
      id: conversation.id,
      name: conversation.title,
      resourceType: ResourceType.Chat,
    });
  }, [handleOpenActionMenu, conversation.id, conversation.title]);

  // Read/unread toggles the same `read_at` flag the blue dot reads from, so the
  // sidebar refreshes off the resulting `RecentChatsUpdated` broadcast exactly
  // as it does when opening a chat auto-marks it read. Chat-specific and with a
  // single caller, so it stays written out here rather than joining the shared
  // `ResourceMenuItem` vocabulary.
  const readUnreadItem: ItemAction =
    conversation.hasUnreadAssistant === true
      ? {
          label: 'Mark as read',
          icon: 'envelope-open',
          onClick: () =>
            void client.resource.chat.markMessagesAsRead({
              conversationId: conversation.id,
            }),
        }
      : {
          label: 'Mark as unread',
          icon: 'eye-slash',
          onClick: () =>
            void client.resource.chat.markMessagesAsUnread({
              conversationId: conversation.id,
            }),
        };

  // The list rows' own trio, so a chat's actions read the same wherever they
  // are reached from. This used to be a local `{label, run, destructive}` shape
  // — a fourth spelling of `ItemAction` that had to be kept in step by hand.
  const rowActions: ItemAction[] = [
    favoriteMenuItem(ResourceType.Chat, conversation.id),
    readUnreadItem,
    ...shareMoveArchiveItems({
      onShare: () => void shareAction.execute({}),
      onMoveToFolder: () => void moveToFolderAction.execute({}),
      onArchive: () => void archiveResourceAction.execute({}),
    }),
  ];

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        className="cursor-pointer"
        isActive={isActive({ type: 'chat', id: conversation.id })}
      >
        <Link
          to={`/${org.slug}/chats/${conversation.id}`}
          state={{ entrypoint: 'sidebar' }}
        >
          <span
            // The dot stays mounted so it can animate its width open, so the
            // label has to come and go with the state rather than riding along
            // permanently — otherwise every recent chat announced as unread.
            aria-label={
              conversation.hasUnreadAssistant === true
                ? 'Unread assistant message'
                : undefined
            }
            aria-hidden={
              conversation.hasUnreadAssistant === true ? undefined : true
            }
            className={cn(
              '-ml-2 rounded-full bg-primary shrink-0 transition-all duration-300 ease-in-out',
              conversation.hasUnreadAssistant === true
                ? 'h-2 w-2 opacity-100'
                : 'h-2 w-0 opacity-0'
            )}
          />
          {/* `sidebarMenuButtonVariants` reserves the trailing 32px itself, via
              `group-has-data-[sidebar=menu-action]/menu-item:pr-8`, so the title
              ellipsises clear of the `⋯` at every sidebar width rather than
              running underneath it. */}
          {isReplying ? (
            <ShimmerText
              variant="muted"
              className="truncate text-sm font-normal"
            >
              {conversation.title}
            </ShimmerText>
          ) : (
            <span className="truncate">{conversation.title}</span>
          )}
        </Link>
      </SidebarMenuButton>
      <DropdownMenu onOpenChange={(open) => open && claimSelection()}>
        <DropdownMenuTrigger asChild>
          <SidebarMenuAction
            showOnHover
            aria-label="Chat actions"
            className={SIDEBAR_ROW_TRAILING_CENTERED}
          >
            <Ellipsis />
          </SidebarMenuAction>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" side="right" className="w-44">
          {rowActions.map((action) => (
            <DropdownMenuItem
              key={action.label}
              className="cursor-pointer"
              variant={action.variant}
              onSelect={action.onClick}
            >
              {action.icon !== undefined ? <Icon icon={action.icon} /> : null}
              {action.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </SidebarMenuItem>
  );
}

function RecentChatsSkeleton() {
  return (
    <SidebarMenu>
      {[1, 2, 3, 4, 5].map((i) => (
        <SidebarMenuItem key={i}>
          <div className="flex items-center px-2 py-2">
            <Skeleton className="h-4 w-4/5 rounded-full bg-coco-accent" />
          </div>
        </SidebarMenuItem>
      ))}
    </SidebarMenu>
  );
}

function Recents() {
  const appModel = useAppModel();
  const org = useWhoami((s) => s.org);
  const recentChatsStatus = useWatchableValue(appModel.recentChatsStatus);
  const [open, handleOpenChange] = useSidebarSectionOpen(RECENTS_COOKIE_NAME);

  return (
    <Collapsible
      open={open}
      onOpenChange={handleOpenChange}
      // Claims the leftover height only while there's a list to put in it, so
      // collapsed the section is just its own heading.
      className="group/recents flex min-h-0 flex-col data-[state=open]:flex-1"
    >
      <SidebarGroup
        className="min-h-0 flex-1 group-data-[collapsible=icon]:opacity-0 group-data-[collapsible=icon]:pointer-events-none"
        style={{ transition: collapseTransition('opacity') }}
      >
        {/* An explicit 11.7px, because the theme's scale has nothing between
            `text-xs` (10.5px) and `text-sm` (13px): at xs the heading read as
            fine print beside its own 13px rows, and at sm it matched them and
            stopped reading as a heading at all. Weight and the muted
            `foreground/55` do the rest of the work. */}
        <SidebarGroupLabel asChild className="shrink-0 pr-2 text-[0.9rem]">
          {/* `asChild` puts `data-sidebar="group-label"` on the trigger, which is
              what stops a tap here from closing the whole mobile sidebar. */}
          <CollapsibleTrigger className="w-full cursor-pointer hover:text-sidebar-foreground">
            Recent chats
            <ChevronDown
              aria-hidden="true"
              className="ml-auto transition-transform duration-200 group-data-[state=closed]/recents:-rotate-90"
            />
          </CollapsibleTrigger>
        </SidebarGroupLabel>
        <CollapsibleContent className="flex min-h-0 flex-1 flex-col">
          <SidebarScrollableContent>
            <SidebarGroupContent>
              {recentChatsStatus === 'loading' ? (
                <RecentChatsSkeleton />
              ) : (
                <RecentChatsContent />
              )}
            </SidebarGroupContent>
          </SidebarScrollableContent>
          <Link
            to={`/${org.slug}/chats`}
            className="shrink-0 mt-1 flex items-center gap-1.5 rounded-md px-2 py-2 text-sm font-medium text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground transition-colors"
          >
            See all chats
            <ChevronRight className="h-4 w-4" />
          </Link>
        </CollapsibleContent>
      </SidebarGroup>
    </Collapsible>
  );
}

function CreditBalanceDisplay() {
  const appModel = useAppModel();
  const creditBalance = useWatchableValue(appModel.creditBalance);
  const { showCredits } = useWatchableValue(appModel.uiPreferences);
  const formattedCredits = Number.isInteger(creditBalance)
    ? creditBalance.toLocaleString()
    : creditBalance.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 1,
      });

  if (!showCredits) {
    return null;
  }

  return (
    <div
      className="px-4 pt-3 pb-2 overflow-hidden max-h-20 group-data-[collapsible=icon]:max-h-0 group-data-[collapsible=icon]:pt-0 group-data-[collapsible=icon]:pb-0 group-data-[collapsible=icon]:opacity-0"
      style={{
        transition: collapseTransition('max-height', 'padding', 'opacity'),
      }}
    >
      <p className="text-xs font-medium text-sidebar-foreground/70">Credits</p>
      <p className="text-sm font-medium text-sidebar-foreground flex items-baseline gap-1">
        {formattedCredits}
        <span className="text-xs text-sidebar-foreground/70">cr</span>
      </p>
    </div>
  );
}

const getInitials = (name: string | null | undefined): string => {
  if (!name) {
    return 'U';
  }
  const parts = name
    .split(' ')
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
  if (parts.length === 0) {
    return 'U';
  }
  if (parts.length === 1) {
    return parts[0]!.slice(0, 1).toUpperCase();
  }
  return `${parts[0]![0]}${parts[1]![0]}`.toUpperCase();
};

const MOBILE_SIDEBAR_CLOSE_ANIMATION_MS = 360;

function AccountMenu() {
  const { open, isMobile, openMobile, setOpenMobile } = useSidebar();
  const { user: workosUser, switchToOrganization } = useAuth();
  const whoami = useWhoami((s) => s.whoami);
  const org = useWhoami((s) => s.org);
  const changeOrg = useWhoami((s) => s.actions.changeOrg);
  const client = useTenantedClient();
  const { logout } = useLogout();
  const { openSettings } = useSettingsModal();
  const [menuOpen, setMenuOpen] = React.useState(false);

  const { data: plan } = useQuery({
    queryKey: queryKeys.settings.org.plan(org.id),
    queryFn: async () => Assert.assertOk(await client.org.getPlan({})),
    staleTime: 60_000,
  });

  const displayName =
    [workosUser?.firstName, workosUser?.lastName].filter(Boolean).join(' ') ||
    whoami.name ||
    whoami.email;
  const profileImageUrl = workosUser?.profilePictureUrl ?? '';
  const planName = plan?.billingPlanName ?? 'Plan';
  const sortedOrgs = [...whoami.orgs].sort((a, b) =>
    a.name.localeCompare(b.name)
  );

  const closeAccountMenuOnMobile = React.useCallback(() => {
    if (isMobile) {
      setMenuOpen(false);
    }
  }, [isMobile]);

  const handleOrgSwitch = async (targetOrg: (typeof whoami.orgs)[number]) => {
    closeAccountMenuOnMobile();

    if (targetOrg.id === org.id) {
      return;
    }

    if (workosUser && targetOrg.workosId) {
      await switchToOrganization({ organizationId: targetOrg.workosId });
    }

    changeOrg(targetOrg.id);
  };

  const handleOpenSettings = React.useCallback(
    (initialView?: 'billing') => {
      if (isMobile && openMobile) {
        setMenuOpen(false);
        setOpenMobile(false);
        window.setTimeout(() => {
          openSettings(initialView);
        }, MOBILE_SIDEBAR_CLOSE_ANIMATION_MS);
        return;
      }

      openSettings(initialView);
    },
    [isMobile, openMobile, openSettings, setOpenMobile]
  );

  return (
    <SidebarMenu className="gap-0">
      <SidebarMenuItem>
        <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground !cursor-pointer w-full rounded-none h-20"
              style={{
                paddingLeft: open ? 16 : 6,
                paddingRight: open ? 16 : 0,
                gap: open ? 8 : 0,
                transition: collapseTransition('padding', 'gap'),
              }}
              data-account-trigger="true"
              data-state={menuOpen ? 'open' : 'closed'}
            >
              <Avatar
                className="rounded-full shrink-0"
                style={{ width: 36, height: 36, minWidth: 36 }}
              >
                <AvatarImage src={profileImageUrl} alt={displayName} />
                <AvatarFallback className="rounded-full">
                  {getInitials(displayName)}
                </AvatarFallback>
              </Avatar>
              <div
                className="grid flex-1 text-left text-sm leading-tight gap-0 min-w-0 overflow-hidden group-data-[collapsible=icon]:absolute group-data-[collapsible=icon]:pointer-events-none"
                style={{
                  opacity: open ? 1 : 0,
                  transition: open
                    ? collapseTransition('opacity')
                    : 'opacity 0ms',
                }}
              >
                <span className="truncate font-medium text-sm leading-tight">
                  {displayName}
                </span>
                <span className="truncate text-xs leading-tight text-sidebar-foreground/70">
                  {planName}
                </span>
              </div>
              <ChevronsUpDown
                className="h-5 w-5 text-sidebar-foreground/70 shrink-0 group-data-[collapsible=icon]:absolute group-data-[collapsible=icon]:pointer-events-none"
                style={{
                  opacity: open ? 1 : 0,
                  transition: open
                    ? collapseTransition('opacity')
                    : 'opacity 0ms',
                }}
              />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-80 rounded-lg z-50 border-border shadow-sm py-1"
            side="top"
            align="start"
            sideOffset={8}
            collisionPadding={12}
          >
            <DropdownMenuLabel className="p-3">
              <div className="flex items-center gap-3">
                <Avatar className="h-9 w-9 rounded-full">
                  <AvatarImage src={profileImageUrl} alt={displayName} />
                  <AvatarFallback className="rounded-full">
                    {getInitials(displayName)}
                  </AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight gap-0">
                  <span className="truncate font-medium text-sm leading-tight">
                    {displayName}
                  </span>
                  <span className="truncate text-xs leading-tight text-muted-foreground">
                    {planName}
                  </span>
                </div>
              </div>
            </DropdownMenuLabel>

            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs text-muted-foreground pt-2 pb-1">
              Organizations
            </DropdownMenuLabel>
            <div className="max-h-52 overflow-y-auto">
              {sortedOrgs.map((orgOption) => {
                const isCurrentOrg = orgOption.id === org.id;
                return (
                  <DropdownMenuItem
                    key={orgOption.id}
                    className="cursor-pointer py-2.5"
                    onClick={() => void handleOrgSwitch(orgOption)}
                  >
                    <Building2 className="h-5 w-5" />
                    <span className="flex-1 truncate">{orgOption.name}</span>
                    {isCurrentOrg && <Check className="h-5 w-5 text-primary" />}
                  </DropdownMenuItem>
                );
              })}
            </div>

            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs text-muted-foreground pt-2 pb-1">
              Account
            </DropdownMenuLabel>
            <DropdownMenuItem
              className="cursor-pointer py-2.5"
              onClick={() => handleOpenSettings()}
            >
              <Settings className="h-5 w-5" />
              <span>Settings</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              className="cursor-pointer py-2.5"
              onClick={() => handleOpenSettings('billing')}
            >
              <CreditCard className="h-5 w-5" />
              <span>Billing</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs text-muted-foreground pt-2 pb-1">
              Help
            </DropdownMenuLabel>
            <DropdownMenuItem
              className="cursor-pointer py-2.5"
              onClick={() => {
                closeAccountMenuOnMobile();
                window.open(
                  'https://cotera.co/docs',
                  '_blank',
                  'noopener,noreferrer'
                );
              }}
            >
              <BookOpen className="h-5 w-5" />
              <span>Documentation</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              className="cursor-pointer py-2.5"
              onClick={() => {
                closeAccountMenuOnMobile();
                window.Pylon?.('show');
              }}
            >
              <CircleHelp className="h-5 w-5" />
              <span>Support</span>
            </DropdownMenuItem>
            <Protected scopes={[['use', 'superadmin']]}>
              <DropdownMenuSeparator />
              <DropdownMenuSub>
                <DropdownMenuSubTrigger className="cursor-pointer py-2.5">
                  <Ghost className="h-5 w-5" />
                  <span>Superadmin Tools</span>
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="min-w-48">
                  <DropdownMenuItem asChild className="cursor-pointer py-2.5">
                    <Link
                      to={`/${org.slug}/atc-visibility`}
                      onClick={closeAccountMenuOnMobile}
                    >
                      <Ghost className="h-5 w-5" />
                      <span>ATC Visibility</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="cursor-pointer py-2.5">
                    <Link
                      to={`/${org.slug}/bill-engineering-hours`}
                      onClick={closeAccountMenuOnMobile}
                    >
                      <CreditCard className="h-5 w-5" />
                      <span>Bill Engineering Hours</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="cursor-pointer py-2.5">
                    <Link
                      to={`/${org.slug}/materializations`}
                      onClick={closeAccountMenuOnMobile}
                    >
                      <Wrench className="h-5 w-5" />
                      <span>Materializations</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="cursor-pointer py-2.5">
                    <Link
                      to={`/${org.slug}/artifacts`}
                      onClick={closeAccountMenuOnMobile}
                    >
                      <BookOpen className="h-5 w-5" />
                      <span>Artifacts</span>
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            </Protected>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="cursor-pointer py-2.5"
              onClick={() => {
                closeAccountMenuOnMobile();
                void logout();
              }}
            >
              <LogOut className="h-5 w-5" />
              <span>Log out</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

/**
 * ScrollArea wrapper for the sidebar nav + recents with a bottom gradient
 * fade whenever more content is scrollable below (so long recents lists melt
 * into the footer instead of clipping against it).
 */
function SidebarScrollableContent({ children }: { children: React.ReactNode }) {
  const viewportRef = React.useRef<HTMLDivElement | null>(null);
  const [hasMoreAbove, setHasMoreAbove] = React.useState(false);
  const [hasMoreBelow, setHasMoreBelow] = React.useState(false);

  React.useEffect(() => {
    const viewport = viewportRef.current;
    if (viewport === null) {
      return;
    }
    const update = () => {
      setHasMoreAbove(viewport.scrollTop > 1);
      setHasMoreBelow(
        viewport.scrollTop + viewport.clientHeight < viewport.scrollHeight - 1
      );
    };
    update();
    viewport.addEventListener('scroll', update, { passive: true });
    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(viewport);
    // Radix wraps children in a content div; observe it so the fade updates
    // when items load in or the list grows.
    const content = viewport.firstElementChild;
    if (content !== null) {
      resizeObserver.observe(content);
    }
    return () => {
      viewport.removeEventListener('scroll', update);
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      <ScrollArea
        className={cn(
          'min-h-0 flex-1',
          /*
           * Radix lays the viewport's content out as `display: table` with
           * `min-width: 100%`, both inline, which sizes that wrapper to its
           * content instead of to the viewport. A row's `w-full` then resolves
           * against a container the row is itself widening, so a long title
           * never gets a definite width to ellipsise against: the row grows
           * past the sidebar and takes its trailing `⋯` with it, clipped at the
           * edge. Forcing the wrapper back to a block gives titles something to
           * truncate against. `!` because the declaration it beats is inline.
           *
           * This was always true; it only became visible once the rows grew a
           * control pinned to their right edge. Before that the overflow just
           * cropped the tail of a long title and read as intended truncation.
           */
          '[&>[data-slot=scroll-area-viewport]>div]:!block'
        )}
        viewportRef={viewportRef}
        hideScrollbar
      >
        {children}
      </ScrollArea>
      <div
        aria-hidden="true"
        className={cn(
          'pointer-events-none absolute inset-x-0 top-0 h-10 bg-gradient-to-b from-sidebar to-transparent transition-opacity duration-300 group-data-[collapsible=icon]:opacity-0',
          hasMoreAbove ? 'opacity-100' : 'opacity-0'
        )}
      />
      <div
        aria-hidden="true"
        className={cn(
          'pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-sidebar to-transparent transition-opacity duration-300 group-data-[collapsible=icon]:opacity-0',
          hasMoreBelow ? 'opacity-100' : 'opacity-0'
        )}
      />
    </div>
  );
}

export function CocoSidebar({
  ...props
}: React.ComponentProps<typeof Sidebar>) {
  const { setOpenMobile } = useSidebar();
  const isSmallScreen = useMediaQuery('(max-width: 767px)');

  const handleSidebarItemClick = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!isSmallScreen) {
        return;
      }

      const target = event.target as HTMLElement | null;
      if (!target) {
        return;
      }

      // Don't close sidebar when interacting with collapsible triggers
      const isCollapsibleTrigger = target.closest(
        '[data-sidebar="group-label"]'
      );
      if (isCollapsibleTrigger) {
        return;
      }

      const isAccountTrigger = target.closest('[data-account-trigger="true"]');
      if (isAccountTrigger) {
        return;
      }

      const interactiveElement = target.closest('[data-sidebar="menu-button"]');

      if (interactiveElement) {
        setOpenMobile(false);
      }
    },
    [isSmallScreen, setOpenMobile]
  );

  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader
        className="pt-3 pb-[5px] group-data-[collapsible=icon]:px-0"
        style={{ transition: collapseTransition('padding') }}
        onClick={handleSidebarItemClick}
      >
        <CocoHeader />
      </SidebarHeader>
      <SidebarContent
        className="bg-transparent overflow-hidden"
        onClick={handleSidebarItemClick}
      >
        <SidebarGroup
          className="pt-1 group-data-[collapsible=icon]:px-0"
          style={{ transition: collapseTransition('padding') }}
        >
          <NavMenu />
        </SidebarGroup>
        {/* Above Recents: favorites are the rows the user chose to keep, and
            recents are the rows time chose for them. Renders nothing until
            there is at least one, so a workspace that has never starred
            anything sees exactly the sidebar it saw before. */}
        <Favorites />
        <Recents />
      </SidebarContent>
      <SidebarFooter className="p-0 gap-0" onClick={handleSidebarItemClick}>
        <CreditBalanceDisplay />
        <SidebarSeparator
          className="mx-0 group-data-[collapsible=icon]:opacity-0"
          style={{ transition: collapseTransition('opacity') }}
        />
        <AccountMenu />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
